import { DirectionalLight, DirectionalLightHelper, Vector3 } from "three";
import { geolocations, stadiumInfo } from "../../../constants/geolocations";
import { Context } from "../../context";
import { GPULevel } from "../../util/benchmark";

import { getPosition } from "suncalc";
import { Skybox } from "./skybox";
import { PoserClock } from "../../util/clock";

const MS_IN_HOUR = 1000 * 60 * 60;

const TEN_DEG = (10 * Math.PI) / 180;
const ray = new Vector3(0, 0, -1);
const unitX = new Vector3(1, 0, 0);
const unitY = new Vector3(0, 1, 0);

const mapSize = 2048;

export class LightRig {
  static defaultVenueId = 1;

  skybox: Skybox;

  private sun: DirectionalLight;
  private sunHelper: DirectionalLightHelper;

  private venueInfo: stadiumInfo;

  private contextRef: Context;

  constructor(context: Context) {
    this.sun = new DirectionalLight(undefined, context.state.sunIntensity);
    this.sunHelper = new DirectionalLightHelper(this.sun);
    // TODO: decide on and benchmark shadows
    this.sun.shadow.bias = context.state.bias;
    this.sun.shadow.mapSize.width = mapSize;
    this.sun.shadow.mapSize.height = mapSize;
    this.sun.castShadow = true;

    this.sunHelper.visible = false;
    context.scene.add(this.sunHelper);

    this.skybox = new Skybox(context);

    const venue = context.gameState.getVenue();
    if (!venue) {
      console.error(
        "falling back to default venue for lighting",
        context.gameState
      );
    }
    const venueId = venue?.id || LightRig.defaultVenueId;
    this.venueInfo =
      geolocations.find((loc) => loc.id === venueId) || geolocations[0];

    this.contextRef = context;
  }

  update(clock: PoserClock) {
    const debugOffsetHr = this.contextRef.state.lightTimeDebug
      ? this.contextRef.state.lightTimeOffset
      : undefined;
    const dOffset = debugOffsetHr ? debugOffsetHr * MS_IN_HOUR : 0;

    this.updateSunPosition(clock.realMs + dOffset);
    this.skybox.update(clock.getTimeOfDay(debugOffsetHr));
  }

  helpersOn(on: boolean) {
    this.sunHelper.visible = on;
  }

  private updateSunPosition(timestamp: number) {
    let sunPos = getPosition(
      // NB: we pass in pure timestamp here to avoid js Date messing up timezones
      //@ts-ignore
      timestamp,
      this.venueInfo.latitude,
      this.venueInfo.longitude
    );

    if (sunPos.altitude > TEN_DEG) {
      ray.set(0, 0, -1);
      ray.applyAxisAngle(unitX, sunPos.altitude);
      ray.applyAxisAngle(
        unitY,
        -sunPos.azimuth + Math.PI + (Math.PI * this.venueInfo.azimuth) / 180
      );
      ray.negate().normalize();
    } else {
      // Sun is below 10 deg elevation OR venue d.n.e. OR Tropicana
      // Just point straight down for now
      ray.set(0, -1, 0);
    }

    ray.multiplyScalar(this.contextRef.state.lightMargin);
  }
}
